import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { IconClockSmallSvg, IconInfoSvg } from '../../../assets/tournaments/redesign';
import TrophyIcon from '../../../assets/tournaments/registeredTrophy.svg';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { GradientFill } from '../common/GradientFill';
import { Pressable } from '../common/Touchable';
import type { SnG } from '../Middle/TournamentsSection';
import {
	RED_BUTTON_FILL,
	redGlow,
	TOURNAMENT_FONT_CONDENSED,
	tournamentColors,
	tournamentTarget,
} from './tournamentModalTheme';
import { formatCountdown, useCountdown } from './useCountdown';

// Figma: "Registered Tournaments" (2747:16906) - 361x98.3 under the tournaments carousel.
const DESIGN_WIDTH = 361;

const FONT = 'Sofia Sans';

const colors = {
	plate: '#420A06',
	border: '#FF9E05',
	title: '#FFFF99',
	body: '#FFFFFF',
	divider: 'rgba(255,138,0,0.6)',
} as const;

type Props = {
	/** Width the 361-wide design panel should occupy. */
	width: number;
	title: string;
	emptyLabel: string;
	/** The tournaments the player is registered for; empty shows `emptyLabel`. */
	tournaments?: SnG[];
	/** Row label per tournament. */
	titleOf?: (sng: SnG) => string;
	unregisterLabel?: string;
	/** Tapping a row or its "i" opens the tournament's info modal. */
	onOpen?: (sng: SnG) => void;
	onUnregister?: (sng: SnG) => void;
	/** A fixed panel height: the rows fill what the title leaves and scroll past it. */
	height?: number;
};

export const RegisteredTournamentsPanel = ({
	width,
	title,
	emptyLabel,
	tournaments = [],
	titleOf = sng => String(sng.id),
	unregisterLabel = 'UNREGISTER',
	onOpen,
	onUnregister,
	height,
}: Props) => {
	const scale = width / DESIGN_WIDTH;
	const s = (value: number) => value * scale;
	const dividerId = useSvgId('registeredDivider');
	const styles = createStyles(scale);

	if (scale <= 0) return null;

	const rows = tournaments.map(sng => (
		<RegisteredRow
			key={String(sng.id)}
			sng={sng}
			label={titleOf(sng)}
			unregisterLabel={unregisterLabel}
			scale={scale}
			styles={styles}
			onOpen={() => onOpen?.(sng)}
			onUnregister={() => onUnregister?.(sng)}
		/>
	));

	return (
		<View style={[styles.panel, height != null && { height }]}>
			<View style={styles.titleRow}>
				<TrophyIcon width={s(22)} height={s(20)} />
				<Text numberOfLines={1} style={styles.title}>
					{title}
				</Text>
			</View>

			{/* The design fades the rule out at both ends, which a plain View cannot do. */}
			<Svg width={s(324)} height={Math.max(1, s(1))} style={styles.divider}>
				<Defs>
					<LinearGradient id={dividerId} x1='0' y1='0' x2='1' y2='0'>
						<Stop offset='0' stopColor={colors.divider} stopOpacity={0} />
						<Stop offset='0.5' stopColor={colors.divider} stopOpacity={1} />
						<Stop offset='1' stopColor={colors.divider} stopOpacity={0} />
					</LinearGradient>
				</Defs>
				<Rect x={0} y={0} width={s(324)} height={Math.max(1, s(1))} fill={`url(#${dividerId})`} />
			</Svg>

			{tournaments.length ? (
				// "Registered List" (5157:12552): one 36-tall row per tournament, 6 apart.
				height == null ? (
					<View style={styles.list}>{rows}</View>
				) : (
					<ScrollView
						style={styles.scroll}
						contentContainerStyle={styles.list}
						showsVerticalScrollIndicator={false}
					>
						{rows}
					</ScrollView>
				)
			) : (
				<View style={height != null && styles.emptyFill}>
					<Text style={styles.empty}>{emptyLabel}</Text>
				</View>
			)}
		</View>
	);
};

/** "Tournament Row / *" (5157:12553): name, countdown, info button and the unregister pill. */
const RegisteredRow = ({
	sng,
	label,
	unregisterLabel,
	scale,
	styles,
	onOpen,
	onUnregister,
}: {
	sng: SnG;
	label: string;
	unregisterLabel: string;
	scale: number;
	styles: ReturnType<typeof createStyles>;
	onOpen: () => void;
	onUnregister: () => void;
}) => {
	const { t } = useTranslation();
	const s = (value: number) => value * scale;
	const left = useCountdown(tournamentTarget(sng));

	return (
		<Pressable
			accessibilityRole='button'
			onPress={onOpen}
			style={({ pressed }) => [styles.row, pressed && styles.pressed]}
		>
			<Text numberOfLines={1} style={styles.rowTitle}>
				{label}
			</Text>
			{tournamentTarget(sng) != null && (
				<View style={styles.timer}>
					<IconClockSmallSvg width={s(12)} height={s(12)} />
					<Text style={styles.timerText}>{formatCountdown(left)}</Text>
				</View>
			)}
			<Pressable
				accessibilityRole='button'
				accessibilityLabel={t('T_SITE_A11Y_TOURNAMENT_INFO', { defaultValue: 'Tournament info' })}
				onPress={onOpen}
				hitSlop={6}
			>
				<IconInfoSvg width={s(24)} height={s(24)} />
			</Pressable>
			<Pressable
				accessibilityRole='button'
				onPress={onUnregister}
				hitSlop={4}
				style={({ pressed }) => [styles.unregister, pressed && styles.pressed]}
			>
				<GradientFill stops={RED_BUTTON_FILL} radius={s(8)} />
				<Text style={styles.unregisterText}>{unregisterLabel}</Text>
			</Pressable>
		</Pressable>
	);
};

const createStyles = (scale: number) => {
	const s = (value: number) => value * scale;

	return StyleSheet.create({
		panel: {
			width: s(DESIGN_WIDTH),
			alignSelf: 'center',
			alignItems: 'center',
			gap: s(10),
			paddingHorizontal: s(15),
			paddingVertical: s(9),
			borderRadius: s(12),
			borderWidth: Math.max(0.5, s(0.65)),
			borderColor: colors.border,
			backgroundColor: colors.plate,
			overflow: 'hidden',
			shadowColor: '#FF0000',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.5,
			shadowRadius: s(30),
			elevation: 8,
		},
		titleRow: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(8),
		},
		title: {
			fontFamily: FONT,
			fontSize: s(13),
			lineHeight: s(26),
			fontWeight: '700',
			letterSpacing: s(0.26),
			color: colors.title,
			textAlign: 'center',
		},
		divider: {
			alignSelf: 'center',
		},
		scroll: {
			width: '100%',
			flex: 1,
		},
		emptyFill: {
			flex: 1,
			justifyContent: 'center',
		},
		list: {
			width: '100%',
			gap: s(6),
		},
		row: {
			height: s(36),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(8),
			paddingHorizontal: s(10),
			borderRadius: s(8),
			borderWidth: Math.max(1, s(1)),
			borderColor: tournamentColors.borderSubtle,
			backgroundColor: tournamentColors.sunken,
		},
		pressed: {
			opacity: 0.8,
		},
		rowTitle: {
			flex: 1,
			fontFamily: TOURNAMENT_FONT_CONDENSED,
			fontWeight: '700',
			fontSize: s(14),
			lineHeight: s(14 * 1.2),
			color: tournamentColors.gold50,
		},
		timer: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(4),
		},
		timerText: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(14),
			lineHeight: s(14),
			letterSpacing: s(-0.14),
			color: tournamentColors.accent,
		},
		unregister: {
			height: s(24),
			paddingHorizontal: s(8),
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: s(8),
			borderWidth: Math.max(1, s(1)),
			borderColor: tournamentColors.buttonRim,
			// No overflow clip: on iOS it hides the view's own glow, and GradientFill rounds itself.
			...redGlow(s(5)),
		},
		unregisterText: {
			fontFamily: FONT,
			fontWeight: '900',
			fontSize: s(10),
			lineHeight: s(12),
			letterSpacing: s(0.6),
			color: tournamentColors.accent,
		},
		empty: {
			// The design centres the copy in a 32-tall box, which is what gives the panel its height.
			width: s(314),
			height: s(32),
			fontFamily: FONT,
			fontSize: s(12),
			lineHeight: s(32),
			color: colors.body,
			textAlign: 'center',
		},
	});
};

export default RegisteredTournamentsPanel;
